import React, {
  useRef,
  useEffect,
  useState,
  createContext,
  useContext,
} from 'react';
import { Box } from '@chakra-ui/react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const MotionBox = motion(Box);

// Context per passare inView ai children
const InViewContext = createContext(false);

export const useSectionInView = () => useContext(InViewContext);

const WrappedSection = ({
  children,
  bg,
  bgGradient,
  bgImage,
  id,
  position,
  overflow,
}) => {
  const ref = useRef(null);
  const [shouldReduceMotion] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    return false;
  });
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true, // Cambiato a true per evitare re-animazioni
  });

  // Scroll-based animations (solo per parallax leggero, non per opacity principale)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const parallaxY = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    shouldReduceMotion ? [0, 0, 0] : [20, 0, -20]
  );

  // Combine refs
  useEffect(() => {
    if (ref.current) {
      inViewRef(ref.current);
    }
  }, [inViewRef]);

  return (
    <InViewContext.Provider value={inView}>
      <MotionBox
        ref={ref}
        minH="100vh"
        w="100%"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        position={position || 'relative'}
        id={id}
        overflow={overflow || 'hidden'}
        initial={shouldReduceMotion ? {} : { opacity: 0, y: 30 }}
        animate={shouldReduceMotion ? {} : { opacity: 1, y: 0 }}
        transition={
          shouldReduceMotion ? {} : { duration: 0.6, ease: 'easeOut' }
        }
        style={shouldReduceMotion ? {} : { y: parallaxY }}
      >
        {/* Background color layer */}
        {(bg || bgGradient) && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg={bg}
            bgGradient={bgGradient}
            zIndex={0}
          />
        )}
        {/* Background image layer (above color, below text) */}
        {bgImage && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            opacity={0.3}
            zIndex={1}
            pointerEvents="none"
            style={{
              backgroundImage: `url(${bgImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
        )}
        {/* Content layer (above everything) */}
        <Box position="relative" zIndex={2} w="100%">
          {children}
        </Box>
      </MotionBox>
    </InViewContext.Provider>
  );
};

export default WrappedSection;
